import Modal from '@/components/Modal/Modal';
import { BaseSyntheticEvent, FC } from 'react';
import * as yup from 'yup';
import { isAddress } from 'viem';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '@/components/toast/useToast';
import { usePostWallet } from '@/services/wallets/queries/usePostWallet';
import Input from '@/components/Input/Input';
import ErrorText from '@/components/ErrorText';
import Label from '@/components/Label';
import Button, { ButtonVariants } from '@/components/new-design/Button';
import Checkbox from '@/components/new-design/Checkbox';
import useShowToast from '@/hooks/useShowToast';
import * as Sentry from '@sentry/react';

interface AddWalletModalProps {
  open: boolean;
  onClose: () => void;
}

interface FormInputs {
  mainWallet: boolean;
  label: string;
  walletAddress: string;
}

const validationSchema = yup
  .object({
    mainWallet: yup.boolean().required(),
    label: yup.string().required(),
    walletAddress: yup
      .string()
      .test('address', 'Enter valid address', (value) => !!value && isAddress(value))
      .required('Wallet Address is required'),
  })
  .required();

const AddWalletModal: FC<AddWalletModalProps> = ({ open, onClose }) => {
  const { toast } = useToast();

  const { mutate, isSuccess, isError, isPending } = usePostWallet();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    setValue,
    watch,
  } = useForm<FormInputs>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      walletAddress: '',
      label: '',
      mainWallet: false,
    },
  });

  const onSubmit = async (data: FormInputs, e?: BaseSyntheticEvent<object, any, any>) => {
    e?.preventDefault();

    try {
      mutate({
        wallet: data.walletAddress,
        label: data.label,
        primary: data.mainWallet,
      });
    } catch (err) {
      toast({ description: 'Error adding a new wallet' });
      Sentry.captureException(err);
    }
  };

  useShowToast({
    isSuccess,
    isError,
    successMessage: 'Wallet added successfully',
    errorMessage: 'Error adding a new wallet',
    callbackSuccess: onClose,
  });

  const mainWalletChecked = watch('mainWallet') || false;

  return (
    <Modal
      className="md:min-w-[500px]"
      open={open}
      setClose={onClose}
      zIndex={100}
      titleComponent={
        <Modal.Title
          onClose={onClose}
          title="Add Additional Wallet"
          subtitle="Select your wallet from one of the supported options"
        />
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <Checkbox
              text="This is my main wallet"
              checked={mainWalletChecked}
              onChange={(e) => setValue('mainWallet', (e.target as HTMLInputElement).checked)}
              className="hover:cursor-pointer"
              disabled={false}
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs text-white text-opacity-50" semiBold>
              Wallet Address
            </Label>
            <Input
              placeholder="e.g. 0xf54df9k21jjsl2841sfg2146us9"
              {...register('walletAddress')}
              error={!!errors.walletAddress}
              type="string"
              className="w-full font-semibold text-ph-dirty-white"
            />
            {errors.walletAddress?.message && <ErrorText>* {errors.walletAddress.message}</ErrorText>}
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-xs text-white text-opacity-50" semiBold>
              Wallet Name (optional)
            </Label>
            <Input
              placeholder="e.g. My new wallet"
              {...register('label')}
              type="string"
              className="w-full font-semibold text-ph-dirty-white"
            />
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              type="submit"
              variant={ButtonVariants.primary}
              disabled={!isDirty || !isValid || isPending}
              isLoading={isPending}
            >
              Add Wallet
            </Button>
            <Button onClick={onClose} variant={ButtonVariants.secondary}>
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddWalletModal;
